import React, { useEffect } from 'react';
import { DragHandle } from '@components/drag-handle';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import classNames from 'classnames';

import { ModalV2Props } from './modal-v2.types';
import { useDragToClose } from './use-drag-to-close';

/**
 * Modal component using Headless UI v2.1+ API with smooth transitions
 *
 * Features:
 * - iOS-like spring animations with cubic-bezier curves
 * - Drag to close on mobile devices (ref-based 60fps)
 * - Multiple size variants (sm, md, lg, xl, full)
 * - Support for multiple simultaneous modals with proper z-index stacking
 * - Fully accessible with ARIA attributes
 * - Modern Headless UI v2.1+ Transition API
 *
 * @example
 * ```tsx
 * <ModalV2
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   variant="md"
 *   hasMobileHandle
 * >
 *   <div className="p-6">
 *     <h2>Modal Title</h2>
 *     <p>Modal content</p>
 *   </div>
 * </ModalV2>
 * ```
 */
export const ModalV2 = ({
  isOpen,
  onClose,
  children,
  variant = 'md',
  hasMobileHandle = true,
  enableDragToClose = true,
  maxWidthClass,
  borderRadiusClass = 'rounded-t-lg sm:rounded-lg',
  overlayClassName,
  contentClassName,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  enableMultipleModals = false,
  onAfterLeave,
  zIndex = 40,
  ariaLabel = 'modal',
}: ModalV2Props) => {
  // Drag to close hook
  const { panelRef, isClosing, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, initiateClose } =
    useDragToClose({ isOpen, onClose, enabled: enableDragToClose });

  // Handle Escape key press when closeOnEscape is disabled
  useEffect(() => {
    if (!closeOnEscape && isOpen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      // Capture phase to intercept before Headless UI's listener
      document.addEventListener('keydown', handleKeyDown, true);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [closeOnEscape, isOpen]);

  return (
    <Transition show={isOpen && !isClosing} appear afterLeave={onAfterLeave}>
      <Dialog
        onClose={closeOnBackdropClick ? () => initiateClose('backdrop') : () => {}}
        className='relative'
        aria-label={ariaLabel}
        __demoMode={enableMultipleModals}
        style={{ zIndex }}>
        {/* Backdrop with blur effect and transition */}
        <TransitionChild
          enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div
            className={classNames('fixed inset-0', 'backdrop-overlay', overlayClassName)}
            aria-hidden='true'
            style={{ zIndex }}
          />
        </TransitionChild>

        {/* Content wrapper - positioned at bottom on mobile, center on desktop */}
        <TransitionChild
          enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          enterFrom='translate-y-full'
          enterTo='translate-y-0'
          leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
          leaveFrom='translate-y-0'
          leaveTo='translate-y-full'>
          <div className='fixed inset-0 flex items-end w-screen sm:items-center sm:justify-center' style={{ zIndex }}>
            <DialogPanel
              ref={panelRef}
              className={classNames(
                // Base styles
                'bg-primary relative w-full',
                'h-auto max-h-[95dvh] overflow-auto scrollbar-xceed',
                'sm:align-middle lg:my-8',
                'shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)]',
                'overscroll-contain',
                borderRadiusClass,

                // Size variants
                {
                  'sm:max-w-xl lg:max-w-211.5': variant === 'lg' && !maxWidthClass,
                  'sm:w-xl lg:max-w-(--breakpoint-md)': variant === 'md' && !maxWidthClass,
                  'sm:max-w-md': variant === 'sm' && !maxWidthClass,
                  'sm:max-w-4xl lg:max-w-7xl': variant === 'xl' && !maxWidthClass,
                  'max-w-full max-h-full h-full w-full rounded-none': variant === 'full' && !maxWidthClass,
                  [`${maxWidthClass}`]: !!maxWidthClass,
                },
                contentClassName
              )}>
              {/* Mobile drag zone — full-width tap/drag target */}
              {hasMobileHandle && (
                <DragHandle
                  className='sm:hidden'
                  onClose={() => initiateClose('drag')}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchCancel}
                />
              )}

              {/* Modal content */}
              {children}
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};
